import { useSeo } from '../lib/seo';
/**
 * /half-days/:slug — public view of a single curated 3-hour itinerary.
 * Shareable URL: the slug never changes, the daily cache regenerates at midnight UTC.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Copy, Share2 } from 'lucide-react';
import api from '../lib/api';
import ItineraryView from '../components/ItineraryView';

export default function HalfDayDetail() {
  const { slug } = useParams();
  const [result, setResult] = useState(null);
  useSeo(
    result?.title ? `${result.title} — Half-Day Itinerary | Go Leicestershire` : null,
    result?.summary
  );
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setResult(null); setNotFound(false);
    api.get(`/half-day-itineraries/${slug}`)
      .then((r) => setResult(r.data))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  function copyLink() {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  if (notFound) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center" data-testid="half-day-not-found">
        <h1 className="font-display text-4xl mb-3">Itinerary not found</h1>
        <p className="text-muted mb-6">This half-day loop may have been retired.</p>
        <Link to="/half-days" className="bg-heritage text-base px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2">
          See all half-days
        </Link>
      </div>
    );
  }
  if (!result) return <div className="py-32 text-center text-muted">Loading…</div>;

  const preset = result.preset || {};

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12 md:py-16" data-testid={`half-day-detail-${slug}`}>
      <Helmet>
        <title>{`${preset.title || 'Half-day'} · GoLeicestershire`}</title>
        <meta name="description" content={preset.sub || 'A curated 3-hour Leicestershire itinerary.'} />
      </Helmet>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <Link to="/half-days" className="inline-flex items-center gap-2 text-muted hover:text-heritage text-sm" data-testid="half-day-back">
          <ArrowLeft size={16}/> All half-days
        </Link>
        <button
          onClick={copyLink}
          data-testid="half-day-share"
          className="bg-charcoal text-base px-4 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2 hover:bg-heritage transition-colors"
        >
          {copied ? <><Copy size={13}/> Copied!</> : <><Share2 size={13}/> Share this loop</>}
        </button>
      </div>

      {preset.title && (
        <div className="mb-10">
          <div className="label text-mustard mb-3">{preset.region_label}</div>
          <h1 className="font-display text-5xl lg:text-6xl leading-[0.95] mb-3">{preset.title}</h1>
          <p className="text-xl text-charcoal/75 max-w-2xl">{preset.sub}</p>
        </div>
      )}

      <ItineraryView result={result} />
    </div>
  );
}
